import React, { useState, useEffect } from "react";
import { Table, Button, Icon, Popup, Form, Modal } from "semantic-ui-react";
import { toast, ToastContainer } from "react-toastify";
import { LoadingSpinner, BasicModal, ConfirmDialog } from "../../../Shared";
import { EPP, Site } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { useCompany } from "../../../../contexts/CompanyContext";
import { EPPForm } from "../EPPForm";
import searchSvg from "../../../../assets/svg/search.svg";
import "./ListEPP.scss";

const eppController  = new EPP();
const siteController = new Site();

const CATEGORY_LABELS = {
  cabeza:       "Cabeza",
  ojos_cara:    "Ojos y cara",
  oidos:        "Auditiva",
  respiratoria: "Respiratoria",
  manos:        "Manos",
  pies:         "Pies",
  cuerpo:       "Cuerpo",
  anticaidas:   "Anti-caídas",
  otro:         "Otro",
};

export function ListEPP({ active, reload, onReload }) {
  const { accessToken } = useAuth();
  const { company: companyFromContext } = useCompany();
  const noCompanyInCtx = !companyFromContext || companyFromContext === "0" || companyFromContext === 0;

  const [items,       setItems]       = useState(null);
  const [filtered,   setFiltered]    = useState([]);
  const [search,     setSearch]      = useState("");
  const [showModal,  setShowModal]   = useState(false);
  const [selected,   setSelected]    = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmMsg,  setConfirmMsg]  = useState("");
  const [isDelete,    setIsDelete]    = useState(false);

  // Stock modal state
  const [stockModal,   setStockModal]   = useState(false);
  const [stockEpp,     setStockEpp]     = useState(null);
  const [sites,        setSites]        = useState([]);
  const [sitesLoading, setSitesLoading] = useState(false);
  const [stockSite,    setStockSite]    = useState("");
  const [stockQty,     setStockQty]     = useState("");
  const [stockLoc,     setStockLoc]     = useState("");
  const [stockSaving,  setStockSaving]  = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setItems(null);
        const res = await eppController.getEPPs(accessToken, active);
        setItems(res);
        setFiltered(res);
      } catch {
        setItems([]);
        setFiltered([]);
      }
    })();
  }, [active, reload]);

  useEffect(() => {
    if (!items) return;
    if (!search) { setFiltered(items); return; }
    const q = search.toLowerCase();
    setFiltered(items.filter(e =>
      e.code?.toLowerCase().includes(q) ||
      e.name?.toLowerCase().includes(q) ||
      e.standard?.toLowerCase().includes(q)
    ));
  }, [search, items]);

  const openCreate = () => { setSelected(null); setShowModal(true); };
  const openEdit   = (e) => { setSelected(e);   setShowModal(true); };

  const openToggle = (e) => {
    setSelected(e);
    setIsDelete(false);
    setConfirmMsg(`¿${e.active ? "Desactivar" : "Activar"} "${e.name}"?`);
    setShowConfirm(true);
  };

  const openDelete = (e) => {
    setSelected(e);
    setIsDelete(true);
    setConfirmMsg(`¿Eliminar "${e.name}"? Esta acción no se puede deshacer.`);
    setShowConfirm(true);
  };

  const openStock = async (e) => {
    setStockEpp(e);
    setStockSite("");
    setStockQty("");
    setStockLoc("");
    setStockModal(true);
    setSitesLoading(true);
    try {
      const res = noCompanyInCtx
        ? await siteController.getSites(accessToken, true)
        : await siteController.getSitesByCompany(accessToken, companyFromContext, true);
      setSites(Array.isArray(res) ? res : []);
    } catch {
      setSites([]);
    } finally {
      setSitesLoading(false);
    }
  };

  const handleStockSiteChange = (siteId) => {
    setStockSite(siteId);
    if (!stockEpp) return;
    const entry = stockEpp.stock?.find(s => String(s.site) === String(siteId));
    setStockQty(entry ? String(entry.quantity) : "");
    setStockLoc(entry ? (entry.location || "") : "");
  };

  const handleStockSave = async () => {
    if (!stockSite) { toast.warning("Seleccioná un sitio"); return; }
    if (stockQty === "" || isNaN(Number(stockQty))) { toast.warning("Cantidad inválida"); return; }
    setStockSaving(true);
    try {
      await eppController.updateEPPStock(accessToken, stockEpp._id, {
        site: stockSite,
        quantity: Number(stockQty),
        location: stockLoc,
      });
      toast.success("Stock actualizado");
      onReload();
      setStockModal(false);
    } catch {
      toast.error("Error al actualizar stock");
    } finally {
      setStockSaving(false);
    }
  };

  const handleToggle = async () => {
    try {
      await eppController.updateEPP(accessToken, selected._id, { active: !selected.active });
      toast.success(selected.active ? "EPP desactivado" : "EPP activado");
      setShowConfirm(false);
      onReload();
    } catch { toast.error("Error al actualizar"); }
  };

  const handleDelete = async () => {
    try {
      await eppController.deleteEPP(accessToken, selected._id);
      toast.success("EPP eliminado");
      setShowConfirm(false);
      onReload();
    } catch (err) { toast.error(err?.msg || "Error al eliminar"); }
  };

  if (items === null) return <LoadingSpinner />;

  return (
    <div className="epp-list">
      <ToastContainer position="top-right" autoClose={3000} />

      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "1rem" }}>
        <div className="search-user-wrapper">
          <img src={searchSvg} alt="search" className="search-user-icon" />
          <input
            className="search-user-input"
            placeholder="Buscar por código, nombre o normativa..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <Button icon className="add-epp-btn" onClick={openCreate}>
          <Icon name="plus" />
        </Button>
      </div>

      {filtered.length === 0 ? (
        <p style={{ color: "#999" }}>Sin resultados</p>
      ) : (
        <Table className="epp-table" selectable>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Código</Table.HeaderCell>
              <Table.HeaderCell>Nombre</Table.HeaderCell>
              <Table.HeaderCell>Categoría</Table.HeaderCell>
              <Table.HeaderCell>Normativa</Table.HeaderCell>
              <Table.HeaderCell textAlign="center">Acciones</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {filtered.map(e => (
              <Table.Row key={e._id}>
                <Table.Cell><code>{e.code}</code></Table.Cell>
                <Table.Cell>{e.name}</Table.Cell>
                <Table.Cell>{CATEGORY_LABELS[e.category] || e.category || "—"}</Table.Cell>
                <Table.Cell style={{ color: "#64748b", fontSize: "0.85em" }}>{e.standard || "—"}</Table.Cell>
                <Table.Cell textAlign="center">
                  <Popup content="Editar" trigger={
                    <Button icon primary size="mini" onClick={() => openEdit(e)}>
                      <Icon name="pencil" />
                    </Button>
                  } />
                  <Popup content="Gestionar stock" trigger={
                    <Button icon size="mini" color="teal" onClick={() => openStock(e)}>
                      <Icon name="warehouse" />
                    </Button>
                  } />
                  <Popup content={e.active ? "Desactivar" : "Activar"} trigger={
                    <Button icon size="mini" color={e.active ? "orange" : "grey"} onClick={() => openToggle(e)}>
                      <Icon name={e.active ? "ban" : "check"} />
                    </Button>
                  } />
                  <Popup content="Eliminar" trigger={
                    <Button icon size="mini" color="red" onClick={() => openDelete(e)}>
                      <Icon name="trash" />
                    </Button>
                  } />
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      {/* Modal editar/crear EPP */}
      <BasicModal
        show={showModal}
        close={() => setShowModal(false)}
        title={selected ? `Editar: ${selected.name}` : "Nuevo EPP"}
        size="small"
      >
        <EPPForm
          epp={selected}
          close={() => setShowModal(false)}
          onReload={onReload}
        />
      </BasicModal>

      {/* Modal gestión de stock */}
      <Modal open={stockModal} onClose={() => setStockModal(false)} size="tiny">
        <Modal.Header>
          Stock — {stockEpp?.name}
        </Modal.Header>
        <Modal.Content>
          {sitesLoading ? (
            <p style={{ color: "#999" }}>Cargando sitios…</p>
          ) : (
            <Form>
              {/* Stock por sitio existente (solo lectura) */}
              {stockEpp?.stock?.length > 0 && (
                <div style={{ marginBottom: "1rem" }}>
                  <p style={{ fontSize: "0.8rem", color: "#64748b", marginBottom: "0.4rem" }}>
                    Stock registrado:
                  </p>
                  <div className="epp-stock-summary">
                    {stockEpp.stock.map(s => {
                      const siteName = sites.find(st => String(st._id) === String(s.site))?.name || String(s.site).slice(-6);
                      return (
                        <div key={String(s.site)} className="epp-stock-summary__row">
                          <span className="epp-stock-summary__site">{siteName}</span>
                          <span className={`epp-stock-summary__qty${s.quantity > 0 ? " epp-stock-summary__qty--ok" : " epp-stock-summary__qty--falta"}`}>
                            {s.quantity} unidades
                          </span>
                          {s.location && <span className="epp-stock-summary__loc">{s.location}</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <Form.Select
                label="Sitio / Planta"
                placeholder="Seleccioná un sitio"
                options={sites.map(s => ({ key: s._id, value: s._id, text: s.name }))}
                value={stockSite}
                onChange={(_, { value }) => handleStockSiteChange(value)}
              />
              <Form.Input
                label="Cantidad disponible"
                type="number"
                min="0"
                value={stockQty}
                onChange={e => setStockQty(e.target.value)}
                placeholder="Ej: 10"
              />
              <Form.Input
                label="Ubicación / Pañol (opcional)"
                value={stockLoc}
                onChange={e => setStockLoc(e.target.value)}
                placeholder="Ej: Pañol central, Almacén B"
              />
            </Form>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setStockModal(false)}>Cancelar</Button>
          <Button primary loading={stockSaving} disabled={stockSaving || sitesLoading} onClick={handleStockSave}>
            Guardar stock
          </Button>
        </Modal.Actions>
      </Modal>

      <ConfirmDialog
        open={showConfirm}
        title={isDelete ? "Eliminar EPP" : (selected?.active ? "Desactivar" : "Activar")}
        message={confirmMsg}
        onCancel={() => setShowConfirm(false)}
        onConfirm={isDelete ? handleDelete : handleToggle}
        confirmLabel="Aceptar"
        cancelLabel="Cancelar"
        confirmDanger={isDelete}
      />
    </div>
  );
}
